/**
 * Users chain identity (compat module name: trade-address).
 *
 * | 登录轨 | users.address_type | 来源 |
 * |--------|-------------------|------|
 * | WALLET / EOA | 1 | user_auth.open_id → users.address |
 * | EMAIL / GOOGLE | 2 | Privy address + privy_wallet_id |
 *
 * HTTP `/trade-address/*` 仅为 users 投影 API，不再读写 user_trade_address 表。
 */

import { addTradeAddress, listTradeAddresses } from '@/api/trade';
import { getWalletAddress, getWalletProviderId } from '@/utils/auth-token';
import { getPrivyEmbeddedSignerAddress } from '@/utils/privy';
import {
  addressesEqual,
  getTradeAddressType,
  hasCustodialTradeAddress,
  hasSelfCustodyTradeAddress,
  isPrivyTradeAddressInList,
  isWalletOnlyEoaTradeAddresses,
  pickCustodialTradeAddressRecord,
  pickTradeAddressRecord,
  pickWalletFromTradeAddressList,
  resolvePrivySignerAddress,
  resolveRedeemExecutionMode,
} from '@/utils/trade-address-core';
import {
  ensureCustodialChainIdentity,
  ensureEoaChainIdentity,
  loadChainIdentityRecords,
} from '@/utils/chain-identity';

export {
  addressesEqual,
  getTradeAddressType,
  hasCustodialTradeAddress,
  hasSelfCustodyTradeAddress,
  isWalletOnlyEoaTradeAddresses,
  resolveRedeemExecutionMode,
  pickCustodialTradeAddressRecord,
  pickTradeAddressRecord,
  resolvePrivySignerAddress,
  pickWalletFromTradeAddressList,
  isPrivyTradeAddressInList,
};

/** Redeem / money-ops capability from users chain identity. */
export {
  resolveChainIdentityCapability,
  resolveChainIdentityCapability as resolveRedeemTradeAddressCapability,
} from '@/utils/chain-identity';

export async function isPrivyTradeAddressRegistered(address, privyWalletId) {
  try {
    const { records } = await loadChainIdentityRecords();
    return isPrivyTradeAddressInList(records, address, privyWalletId);
  } catch (error) {
    console.warn('[trade-address] list failed while checking Privy registration', error);
    return false;
  }
}

export async function isSelfCustodyTradeAddressRegistered(address) {
  const normalizedAddress = String(address ?? '').trim();
  if (!normalizedAddress) return false;
  try {
    const { records } = await loadChainIdentityRecords();
    return records.some((item) => {
      if (!addressesEqual(item?.address, normalizedAddress)) return false;
      if (Number(item?.addressType) === 2) return false;
      if (String(item?.privyWalletId ?? '').trim()) return false;
      return true;
    });
  } catch (error) {
    console.warn('[trade-address] list failed while checking self-custody registration', error);
    return false;
  }
}

export async function fetchTradeAddressFromList(options = {}) {
  const embeddedAddress = String(options.embeddedAddress ?? '').trim();
  const fallbackPrivyWalletId = String(options.privyWalletId ?? '').trim();
  try {
    const { records } = await loadChainIdentityRecords();
    if (embeddedAddress) {
      const resolvedAddress = resolvePrivySignerAddress(embeddedAddress, records);
      const matched = records.find((item) => addressesEqual(item?.address, resolvedAddress));
      return {
        address: resolvedAddress,
        privyWalletId: String(matched?.privyWalletId ?? '').trim() || fallbackPrivyWalletId,
        records,
      };
    }
    const picked = pickTradeAddressRecord(records);
    if (!picked) return { address: '', privyWalletId: '', records };
    return {
      address: String(picked.address ?? '').trim(),
      privyWalletId: String(picked.privyWalletId ?? '').trim(),
      records,
    };
  } catch (error) {
    console.warn('[trade-address] list failed', error);
    if (embeddedAddress) {
      return { address: embeddedAddress, privyWalletId: fallbackPrivyWalletId, records: [] };
    }
    return { address: '', privyWalletId: '', records: [] };
  }
}

export async function resolveTradeAddressFromList() {
  const { address } = await fetchTradeAddressFromList();
  return address;
}

/** EMAIL/GOOGLE：写入 users type=2（Privy 地址 + privy_wallet_id） */
export async function registerPrivyTradeAddress(user, options) {
  return ensureCustodialChainIdentity(user, options);
}

/** WALLET：写入 users type=1（EOA / open_id） */
export async function registerSelfCustodyTradeAddress(walletAddress) {
  return ensureEoaChainIdentity(walletAddress);
}

export async function ensureSelfCustodyTradeAddressAfterLogin() {
  if (getWalletProviderId() === 'privy') return false;
  const address = String(getWalletAddress() ?? '').trim();
  if (!address) return false;
  return registerSelfCustodyTradeAddress(address);
}

export async function assertPrivySignerMatches(expectedAddress) {
  if (getWalletProviderId() !== 'privy') return;
  const expected = String(expectedAddress ?? '').trim();
  if (!expected) return;
  const signerAddress = await getPrivyEmbeddedSignerAddress();
  if (!signerAddress) {
    throw new Error('Privy embedded wallet provider is unavailable');
  }
  if (signerAddress.toLowerCase() !== expected.toLowerCase()) {
    const error = new Error('wallet address mismatch');
    error.code = 'PRIVY_SIGNER_MISMATCH';
    throw error;
  }
}

// Legacy direct API access (prefer chain-identity helpers).
export { addTradeAddress, listTradeAddresses };
